import type { Dayjs, ManipulateType, QUnitType } from 'dayjs';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ko';
import { ServerDate } from './catch-server-date';

dayjs.extend(quarterOfYear);
dayjs.extend(utc);
dayjs.extend(timezone);

type DateFormat =
  | 'dd'
  | 'HH:mm'
  | 'A HH:mm'
  | 'YYYY-MM'
  | 'YYYY-MM-DD'
  | 'YYYY-MM-DD HH'
  | 'YYYY-MM-DD HH:mm'
  | 'YYYY-MM-DD HH:mm:ss'
  | 'YYYY-MM-DD(dd)'
  | 'YYYY-MM-DD(dd) HH:mm'
  | 'YYYY-MM-DDTHH:mm:ss'
  | 'YYYY-MM-DDTHH:mm'
  | 'YYYY.MM.DD'
  | 'YYYY/MM/DD'
  ;

export const current = () => {
  return dayjs(new ServerDate()).tz('Asia/Seoul');
};

export const getNow = (format: DateFormat) => {
  return current().locale('ko').format(format);
};

export const getNowDayjs = (foramt: DateFormat) => {
  return dayjs(getNow(foramt));
};

export const getFormattedDate = (date: string | Dayjs, format: DateFormat) => {
  return dayjs(date).tz('Asia/Seoul').locale('ko').format(format);
};

export const getDateFromToday = (amount: ManipulateType | QUnitType) => {
  return current().subtract(1, amount).format('YYYY-MM-DD');
};

export const getAddedDate = (fromDate: string, amount: number, unit: QUnitType, format?: DateFormat) => {
  return dayjs(fromDate).locale('ko').add(amount, unit).format(format || 'YYYY-MM-DD');
};

export const getBeforeMinutesFrom = (time: string, amount: number, format?: DateFormat) => {
  return getFormattedDate(dayjs(time).subtract(amount, 'minute'), format || 'HH:mm');
};

export const hhmmssTohhmm = (time: string) => {
  const [HH, mm] = time.split(':');
  return `${HH}:${mm}`;
};

export const hhmmssTohhmmAmPm = (time: string) => {
  const [hh, mm] = time.split(':');
  return time < '12:00:00' ? `오전 ${hh}:${mm}` : `오후 ${hh}:${mm}`;
};

export const dayDiffFromCurrent = (date?: string | Dayjs | null) => dayjs(getNow('YYYY-MM-DD')).diff(dayjs(date).format('YYYY-MM-DD'), 'd');
export const hourDiffFromCurrent = (date?: string | Dayjs | null) => dayjs(getNow('YYYY-MM-DD HH')).diff(dayjs(date).format('YYYY-MM-DD HH'), 'h');
export const minuteDiffFromCurrent = (date?: string | Dayjs | null) => dayjs(getNow('YYYY-MM-DD HH:mm')).diff(dayjs(date).format('YYYY-MM-DD HH:mm'), 'm');
export const secondDiffFromCurrent = (date?: string | Dayjs | null) => dayjs(getNow('YYYY-MM-DD HH:mm:ss')).diff(dayjs(date).format('YYYY-MM-DD HH:mm:ss'), 's');

export const dDay = (date?: string | Dayjs | null) => {
  if (!date) return '';
  const diff = dayDiffFromCurrent(date);
  let dayText = 'D';
  if (diff < 0) dayText += diff;
  else if (diff > 0) dayText += `+${diff}`;
  else dayText += '-Day';
  return dayText;
};

export const datetimeToDateAndTime = (datetime?: string | null) => {
  const date = dayjs(datetime);
  return {
    date: getFormattedDate(date, 'YYYY-MM-DD'),
    time: getFormattedDate(date, 'HH:mm'),
  };
};

export const isAfterNow = (date?: string | Dayjs | null) => {
  if (!date) return false;
  return current().isBefore(date);
};

type DateOffset = {
  days?: number,
  minutes?: number,
};

export const getDateStartAt = (sequence: number, offset: DateOffset = {
  days: 0,
  minutes: 0,
}) => {
  const dateStartAt = new Date('2022-11-11T09:00:00.000Z');
  dateStartAt.setDate(
    dateStartAt.getDate() + sequence,
  );

  offset.days && dateStartAt.setDate(
    dateStartAt.getDate() + offset.days,
  );

  offset.minutes && dateStartAt.setMinutes(
    dateStartAt.getMinutes() + offset.minutes,
  );

  return dateStartAt.toISOString();
};

export const getCurrentMonthDates = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  // 다음 달의 0일은 이 달의 마지막 날
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return { start, end };
};
